class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    this.facetFiltersFormId = facetForm.id || 'FacetFiltersForm';
    this.facetFiltersFormMobileId = `${this.facetFiltersFormId}Mobile`;
    this.productGridId = this.getAttribute('data-product-grid-id') || 'ProductGridContainer';

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    this.filterData = [];
    this.searchParamsInitial = window.location.search.slice(1);
    this.searchParamsPrev = window.location.search.slice(1);
    this.setListeners();
  }

  setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : this.searchParamsInitial;
      if (searchParams === this.searchParamsPrev) return;
      this.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  renderPage(searchParams, event, updateURLHash = true) {
    this.searchParamsPrev = searchParams;
    const sections = this.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById(this.productGridId).querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    // Cached sections mount ArnLoadMore synchronously; it reads sort/filters from the URL.
    if (updateURLHash) this.updateURLHash(searchParams);

    sections.forEach((section) => {
      // For collection pages, we need to use the collection URL (the current URL)
      let baseUrl = window.location.pathname;
      if (this.facetFiltersFormId === 'SearchFacetFiltersForm') {
        // For search pages, we need to use the search URL
        baseUrl = window.routes.search_url;
        this.productsSearchResults = this.productsSearchResults || document.querySelector('[data-predictive-search-results]');
        this.productsSearchResults.removeAttribute('open');
        this.productsSearchResults.setAttribute('loading', true);
      }

      const url = `${baseUrl}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      this.filterData.some(filterDataUrl)
        ? this.renderSectionFromCache(filterDataUrl, event, searchParams)
        : this.renderSectionFromFetch(url, event, searchParams);
    });
  }

  renderSectionFromFetch(url, event, searchParams) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        this.filterData = [...this.filterData, { html, url }];
        this.renderFilters(html, event);
        this.renderProductGridContainer(html);
        const productCount = this.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);

        this.sendTealiumEvent(productCount, searchParams);
      });
  }

  renderSectionFromCache(filterDataUrl, event, searchParams) {
    const html = this.filterData.find(filterDataUrl).html;
    this.renderFilters(html, event);
    this.renderProductGridContainer(html);
    const productCount = this.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);

    this.sendTealiumEvent(productCount, searchParams);
  }

  sendTealiumEvent(productCount, searchParams) {
    // Tealium "Filter Applied" Event published on Facets update
    searchParams = new URLSearchParams(searchParams);
    let facetValuesString = [];
    for (const [key, value] of searchParams) {
      if (key !== 'q') facetValuesString.push(`${key}=${value}`);
    }

    const tealiumPubObj = {
      "Events_SearchFiltering": "1",
      "Search_ResultItemsQnt": `${productCount}`,
      "Search_FacetValues_String": facetValuesString.join('|')
    }

    publish(PUB_SUB_EVENTS.facetsUpdate, tealiumPubObj);
  }

  renderProductGridContainer(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    document.getElementById(this.productGridId).innerHTML = doc.getElementById(this.productGridId).innerHTML;

    // If search, update also the results count
    if (this.facetFiltersFormId === 'SearchFacetFiltersForm') {
      const resultsCount = doc.querySelector('[data-search-results-count]');
      const totalResults = doc.querySelector('[data-total-results]');
      const resultsCountElement = document.querySelector('[data-results-count]');

      if (totalResults) {
        const totalCount = parseInt(totalResults.getAttribute('data-total-results'), 10);
        document.querySelector('[data-predictive-search-results-count]').textContent = "(" + totalCount + ")";
      }
      if (resultsCount) {
        resultsCountElement.textContent = resultsCount.textContent;
      } else {
        resultsCountElement.textContent = '';
      }

      this.productsSearchResults.removeAttribute('loading');
      this.productsSearchResults.setAttribute('open', true);
    }

    document
      .getElementById(this.productGridId)
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    return count;
  }

  renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      `#${this.facetFiltersFormId} .js-filter, #${this.facetFiltersFormMobileId} .js-filter`
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      `#${this.facetFiltersFormId} .js-filter, #${this.facetFiltersFormMobileId} .js-filter`
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    this.renderActiveFacets(parsedHTML);
    this.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        this.renderCounts(countsToRender, event.target.closest('.js-filter'));
        this.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    this.toggleActiveFacets(false);
  }

  renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById(this.facetFiltersFormMobileId).closest('menu-drawer').bindEvents();
  }

  renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    this.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === this.facetFiltersFormMobileId;

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === this.facetFiltersFormId) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === this.facetFiltersFormMobileId) {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    this.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    this.renderPage(url);
  }
}

customElements.define('facet-filters-form', FacetFiltersForm);


class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);


class ArnFacets extends HTMLElement {
  connectedCallback() {
    this.barFiltersFormId = this.getAttribute('data-bar-filters-form-id') || 'BarFiltersForm';
    this.facetFiltersFormId = this.querySelector('form')?.id || 'FacetFiltersForm';
    
    // Handle sort buttons functionality
    const sortButtons = document.querySelectorAll('.js-sort-option');
    const sortInput = document.getElementById('sort_by_input');

    sortButtons.forEach((button) => {
      // Update active button styling on page load
      if (button.dataset.active === 'true') {
        button.classList.remove('tw-arn-btn-outline-sm');
        button.classList.add('tw-arn-btn-filled-sm');
        button.classList.add('tw-arn-btn-secondary');
      }

      button.addEventListener('click', function () {
        // Remove active state from all buttons
        sortButtons.forEach((btn) => {
          btn.classList.remove('tw-arn-btn-filled-sm');
          btn.classList.remove('tw-arn-btn-secondary');
          btn.classList.add('tw-arn-btn-outline-sm');
          btn.dataset.active = 'false';
        });

        // Add active state to clicked button
        this.classList.remove('tw-arn-btn-outline-sm');
        this.classList.add('tw-arn-btn-filled-sm');
        this.classList.add('tw-arn-btn-secondary');
        this.dataset.active = 'true';

        // Update hidden input and trigger form submission
        sortInput.value = this.dataset.sortValue;

        // Trigger form submission using Dawn's standard method
        const form = this.closest('form');
        const event = new Event('input', { bubbles: true });
        form.dispatchEvent(event);
        
        // Update filter count (sort doesn't affect count, but for consistency)
        setTimeout(updateFilterCount, 10);
      });
    });

    // Handle boolean filter buttons (toggle functionality)
    const booleanFilterButtons = document.querySelectorAll('.js-boolean-filter');

    booleanFilterButtons.forEach((button) => {
      // Update active button styling on page load
      if (button.dataset.active === 'true') {
        button.classList.remove('tw-arn-btn-outline-sm');
        button.classList.add('tw-arn-btn-filled-sm');
        button.classList.add('tw-arn-btn-secondary');
      }

      button.addEventListener('click', function () {
        const filterParam = this.dataset.filterParam;
        const filterValue = this.dataset.filterValue;
        const form = this.closest('form') || document.getElementById(this.facetFiltersFormId);

        // Check if form exists
        if (!form) {
          console.error('Form not found for boolean filter:', filterParam);
          return;
        }

        // Toggle button state logic
        const isCurrentlyActive = this.dataset.active === 'true';

        if (isCurrentlyActive) {
          // Deactivate filter - remove input completely
          this.classList.remove('tw-arn-btn-filled-sm');
          this.classList.remove('tw-arn-btn-secondary');
          this.classList.add('tw-arn-btn-outline-sm');
          this.dataset.active = 'false';

          // Remove all inputs with this parameter
          const existingInputs = form.querySelectorAll(`input[name="${filterParam}"]`);
          existingInputs.forEach(input => input.remove());

        } else {
          // Activate filter - create new input with TRUE value
          this.classList.remove('tw-arn-btn-outline-sm');
          this.classList.add('tw-arn-btn-filled-sm');
          this.classList.add('tw-arn-btn-secondary');
          this.dataset.active = 'true';

          // Remove any existing inputs for this parameter
          const existingInputs = form.querySelectorAll(`input[name="${filterParam}"]`);
          existingInputs.forEach(input => input.remove());

          // Create new hidden input with TRUE value
          const newInput = document.createElement('input');
          newInput.type = 'hidden';
          newInput.name = filterParam;
          newInput.value = filterValue;
          form.appendChild(newInput);
        }

        // Get reference to ArnFacets instance for method calls
        const arnFacetsInstance = this.closest('arn-facets');
        
        // Sync with bar boolean filters (quick filters)
        if (arnFacetsInstance) {
          arnFacetsInstance.syncBarBooleanFilter(filterParam, this.dataset.active);
          arnFacetsInstance.syncBarFiltersForm(filterParam, filterValue, this.dataset.active);
        }

        // Trigger form submission using Dawn's standard method
        const event = new Event('input', { bubbles: true });
        form.dispatchEvent(event);
        
        // Update filter count after boolean filter change
        setTimeout(updateFilterCount, 10);
      });
    });

    // Handle checkbox changes for active filter counting
    const form = document.getElementById(this.facetFiltersFormId);
    if (form) {
      form.addEventListener('change', function (e) {
        if (e.target.type === 'checkbox') {
          // Update filter counts when checkboxes change
          updateFilterCounts();
          // Update main filter button count
          setTimeout(updateFilterCount, 10);
        }
      });
    }

    // Handle Apply button functionality
    const applyButton = document.querySelector('.js-apply-filters');
    if (applyButton) {
      applyButton.addEventListener('click', function () {
        // Drawer will close automatically due to js-arn-modal-close class
        // Update filter count when applying filters
        setTimeout(updateFilterCount, 10);
      });
    }

    // Initialize filter count on page load
    setTimeout(updateFilterCount, 100);
  }

  /**
   * Synchronizes the bar boolean filter button state with drawer filter
   * @param {string} filterParam - The filter parameter name
   * @param {string} activeState - 'true' or 'false'
   */
  syncBarBooleanFilter(filterParam, activeState) {
    const barButton = document.querySelector(`.js-bar-boolean-filter[data-filter-param="${filterParam}"]`);
    if (barButton) {
      barButton.dataset.active = activeState;
      if (activeState === 'true') {
        barButton.classList.remove('tw-arn-btn-outline-sm');
        barButton.classList.add('tw-arn-btn-filled-sm');
        barButton.classList.add('tw-arn-btn-secondary');
      } else {
        barButton.classList.remove('tw-arn-btn-filled-sm');
        barButton.classList.remove('tw-arn-btn-secondary');
        barButton.classList.add('tw-arn-btn-outline-sm');
      }
    }
  }

  /**
   * Synchronizes the bar filters form with drawer filter state
   * @param {string} filterParam - The filter parameter name
   * @param {string} filterValue - The filter value
   * @param {string} activeState - 'true' or 'false'
   */
  syncBarFiltersForm(filterParam, filterValue, activeState) {
    const barForm = document.getElementById(this.barFiltersFormId);
    if (barForm) {
      // Remove existing inputs for this parameter
      const existingInputs = barForm.querySelectorAll(`input[name="${filterParam}"]`);
      existingInputs.forEach(input => input.remove());

      if (activeState === 'true') {
        // Add new hidden input with TRUE value
        const newInput = document.createElement('input');
        newInput.type = 'hidden';
        newInput.name = filterParam;
        newInput.value = filterValue;
        barForm.appendChild(newInput);
      }
    }
  }
}

/**
 * Updates the active filter count display in accordion titles
 * Counts checked checkboxes and updates the (n) indicator
 */
function updateFilterCounts() {
  const accordions = document.querySelectorAll('.tw-js-filter');

  accordions.forEach((accordion) => {
    const checkboxes = accordion.querySelectorAll('input[type="checkbox"]:checked');
    let titleContainer = accordion.querySelector('h5 .tw-inline-flex');
    const h5Element = accordion.querySelector('h5');

    if (checkboxes.length > 0) {
      if (titleContainer) {
        // Update existing count in parentheses
        titleContainer.textContent = `(${checkboxes.length})`;
        titleContainer.style.display = 'inline-flex';
      } else {
        // Create count container if it doesn't exist
        titleContainer = document.createElement('span');
        titleContainer.className = 'tw-inline-flex tw-items-center tw-justify-center';
        titleContainer.textContent = `(${checkboxes.length})`;
        h5Element.appendChild(titleContainer);
      }
    } else {
      if (titleContainer) {
        // Hide count container if no active filters
        titleContainer.style.display = 'none';
      }
    }
  });
}

/**
 * Updates the filter count display in the main filter button
 * Counts all active filters across all filter types
 */
function updateFilterCount() {
  const filterButton = document.querySelector('.js-arn-modal-btn[data-modal-id="arn-plp-filters-panel"]');
  if (!filterButton) return;

  const facetForm = document.getElementById('FacetFiltersForm');
  if (!facetForm) return;

  let totalActiveFilters = 0;

  // Count checked checkboxes (regular filters)
  const checkedCheckboxes = facetForm.querySelectorAll('input[type="checkbox"]:checked');
  totalActiveFilters += checkedCheckboxes.length;

  // Count active boolean filters (those with data-active="true")
  const activeBooleanButtons = facetForm.querySelectorAll('.js-boolean-filter[data-active="true"]');
  totalActiveFilters += activeBooleanButtons.length;

  // Update button text
  const buttonText = filterButton.textContent.replace(/\s*\(\d+\)$/, ''); // Remove existing count
  const baseText = buttonText.trim();
  
  if (totalActiveFilters > 0) {
    filterButton.textContent = `${baseText} (${totalActiveFilters})`;
  } else {
    filterButton.textContent = baseText;
  }
}

customElements.define('arn-facets', ArnFacets);

function initBarBooleanFilters(facetFiltersFormId = 'FacetFiltersForm') {
  // Handle bar boolean filter buttons
  const barBooleanFilterButtons = document.querySelectorAll('.js-bar-boolean-filter');

  barBooleanFilterButtons.forEach((button) => {
    // Update active button styling on page load
    if (button.dataset.active === 'true') {
      button.classList.remove('tw-arn-btn-outline-sm');
      button.classList.add('tw-arn-btn-filled-sm');
      button.classList.add('tw-arn-btn-secondary');
    }

    button.addEventListener('click', function () {
      const filterParam = this.dataset.filterParam;
      const filterValue = this.dataset.filterValue;
      const formId = this.dataset.formId;
      const form = document.getElementById(formId);
      const mainForm = document.getElementById(facetFiltersFormId);

      if (!form || !mainForm) return;

      // Toggle button state logic
      const isCurrentlyActive = this.dataset.active === 'true';

      if (isCurrentlyActive) {
        // Deactivate filter - remove input from both forms
        this.classList.remove('tw-arn-btn-filled-sm');
        this.classList.remove('tw-arn-btn-secondary');
        this.classList.add('tw-arn-btn-outline-sm');
        this.dataset.active = 'false';

        // Remove from both forms
        [form, mainForm].forEach((targetForm) => {
          const existingInputs = targetForm.querySelectorAll(`input[name="${filterParam}"]`);
          existingInputs.forEach((input) => input.remove());
        });
      } else {
        // Activate filter - create new input with True value in both forms
        this.classList.remove('tw-arn-btn-outline-sm');
        this.classList.add('tw-arn-btn-filled-sm');
        this.classList.add('tw-arn-btn-secondary');
        this.dataset.active = 'true';

        // Remove any existing inputs for this parameter from both forms
        [form, mainForm].forEach((targetForm) => {
          const existingInputs = targetForm.querySelectorAll(`input[name="${filterParam}"]`);
          existingInputs.forEach((input) => input.remove());

          // Create new hidden input with True value
          const newInput = document.createElement('input');
          newInput.type = 'hidden';
          newInput.name = filterParam;
          newInput.value = filterValue;
          targetForm.appendChild(newInput);
        });
      }

      // Sync with drawer boolean filters
      const drawerButton = document.querySelector(`.js-boolean-filter[data-filter-param="${filterParam}"]`);
      if (drawerButton) {
        drawerButton.dataset.active = this.dataset.active;
        if (this.dataset.active === 'true') {
          drawerButton.classList.remove('tw-arn-btn-outline-sm');
          drawerButton.classList.add('tw-arn-btn-filled-sm');
          drawerButton.classList.add('tw-arn-btn-secondary');
        } else {
          drawerButton.classList.remove('tw-arn-btn-filled-sm');
          drawerButton.classList.remove('tw-arn-btn-secondary');
          drawerButton.classList.add('tw-arn-btn-outline-sm');
        }
      }

      // Trigger form submission using Dawn's standard method on main form
      const event = new Event('input', { bubbles: true });
      mainForm.dispatchEvent(event);
      
      // Update filter count after bar filter change
      setTimeout(updateFilterCount, 10);
    });
  });
}